<template>
  <div class="fixed inset-0 z-30 overflow-y-auto bg-white">
    <ArtistPanelHeader :previous-router="{ hash: '' }" :title="title" />
    <div v-if="isInDesignGroupType(currentHash)">
      <ProductNavigator is-single-design-group-type-mode />
    </div>
    <div class="grid-container">
      <DisplayCard
        v-for="(item, index) in cardContentWithCurrentDevice"
        :key="index"
        ref="observeItems"
        :content="item"
      />
      <LoadingSpinner class="col-span-full mx-auto my-30px" :is-loading="isLoading" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref, useTemplateRef, watch } from 'vue'
import { useRoute } from 'vue-router'

import ArtistPanelHeader from '@/components/artistPage/ArtistPanelHeader.vue'
import {
  isInArtworkType,
  isInDesignGroupType,
  isMultiLayersPreviewCollageDisplayCardContent,
} from '@/components/artistPage/expandTypeCheck'
import { type DisplayCardContent } from '@/components/DisplayCards/DisplayCard'
import DisplayCard from '@/components/DisplayCards/DisplayCard.vue'
import LoadingSpinner from '@/components/LoadingSpinner.vue'
import ProductNavigator from '@/components/productNavigator/mobile/ProductNavigator.vue'
import { checkAndResetProduct } from '@/data/hardCode'
import { useCurrentProductDisplay } from '@/data/useNewProductDisplay'
import { ArtworkType, type DesignGroupType } from '@/graphql/creator-platform/generated'
import { useIntersectionObserver } from '@/utils/useIntersectionObserver'

const route = useRoute()
const currentProductDisplay = useCurrentProductDisplay()
const lastItem = ref<HTMLElement | null>(null)
const observeItems = useTemplateRef<Array<InstanceType<typeof DisplayCard>>>('observeItems')
const props = defineProps<{
  cardContents: DisplayCardContent[]
  hasNextPage: boolean
  isLoading: boolean
  title: string
}>()
const emits = defineEmits<{
  fetchArtworksByArtworkType: [value: ArtworkType.Background | ArtworkType.StickerGroup]
  fetchArtworksByDesignGroup: [value: DesignGroupType]
}>()

onMounted(() => {
  if (isInDesignGroupType(currentHash.value)) {
    void checkAndResetProduct(currentHash.value)
  }
  appendMore()
})

onUnmounted(() => {
  stopObserve()
})
watch(
  () => props.cardContents.length,
  () => {
    void assignLastItem()
  },
  { immediate: true },
)

const cardContentWithCurrentDevice = computed((): DisplayCardContent[] => {
  if (currentProductDisplay.value === null) return props.cardContents
  const { designRect, previewMaskUrl, postDesignLayers, preDesignLayers } =
    currentProductDisplay.value
  return props.cardContents.map((e) => {
    return isMultiLayersPreviewCollageDisplayCardContent(e)
      ? {
          ...e,
          preview: {
            ...e.preview,
            designRect: designRect,
            mask: previewMaskUrl,
            postDesignLayers: postDesignLayers,
            preDesignLayers: preDesignLayers,
          },
        }
      : e
  })
})

function appendMore(): void {
  if (!props.hasNextPage) {
    stopObserve()
    return
  }
  if (isInDesignGroupType(currentHash.value)) {
    emits('fetchArtworksByDesignGroup', currentHash.value)
  } else if (isInArtworkType(currentHash.value)) {
    if (currentHash.value === ArtworkType.Collage) return
    emits('fetchArtworksByArtworkType', currentHash.value)
  }
}
async function assignLastItem(): Promise<void> {
  await nextTick()
  const id = props.cardContents[props.cardContents.length - 1]?.id
  if (id === undefined) return
  // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
  lastItem.value = observeItems.value?.find((x) => x.$props.content.id === id)?.$el ?? null
}
const stopObserve = useIntersectionObserver(lastItem, appendMore)

const currentHash = computed(() => {
  return route.hash.replace(/^#/, '')
})
</script>

<style scoped>
.grid-container {
  @apply grid gap-4px md:gap-8px justify-center;
  grid-template-columns: repeat(auto-fill, 180px);
}
@screen md {
  .grid-container {
    grid-template-columns: repeat(auto-fill, 240px);
    transition: max-height 500ms ease;
  }
}
</style>
